import { useQuery } from "@tanstack/react-query";
import { api, isApiError } from "@/lib/api/client";
import type { Me } from "./types";

/**
 * The session is server state read through TanStack Query, not a context
 * store. One key, one fetch, one cache entry — so there is no second copy of
 * "who am I" to drift out of agreement with the cookie.
 */
export const ME_QK = ["me"] as const;

/**
 * Who the session cookie belongs to, or `null` for nobody.
 *
 * A 401 is an ANSWER — "nobody is signed in" — not a failure. Returning null
 * keeps it out of the query's error branch, so the guard renders the login
 * redirect instead of an error boundary.
 *
 * Every other status throws, and that distinction is the whole point: the API
 * answers 503, not 401, when its store is unreachable, precisely so a database
 * restart does not read as "you have been signed out". Collapsing both into
 * null here would discard that and silently sign everyone out during a deploy.
 *
 * Cookie handling is `api()`'s, not this function's: it sets
 * `credentials: "include"`, which is a no-op for the same-origin `/v1` paths
 * this client is restricted to and is kept so a future proxy hop cannot
 * silently drop the session cookie.
 */
export async function fetchMe(): Promise<Me | null> {
  try {
    return await api<Me>("/v1/me");
  } catch (error) {
    if (isApiError(error) && error.status === 401) return null;
    throw error;
  }
}

export function useSession() {
  return useQuery<Me | null>({
    queryKey: ME_QK,
    queryFn: fetchMe,
    // Many components read the session, and without a window each one
    // mounting would fire its own probe. Thirty seconds collapses a
    // navigation burst into a single request while staying far shorter than
    // any membership change a viewer would notice.
    staleTime: 30_000,
    // Returning to a tab is when a session is most likely to have been
    // revoked somewhere else. `RequireSession` keeps the authenticated tree
    // mounted across a revalidation, so this check costs nothing visible and
    // never discards a half-filled form.
    refetchOnWindowFocus: "always",
    // A 401 is already an answer, handled above. Retrying anything else only
    // delays the "can't reach Character Studio" banner the guard renders.
    retry: false,
  });
}
